import { useCallback } from 'react';
import useAuth from './useAuth';
import useAxiosPrivate from './useAxiosPrivate';
import { useAppDispatch, useAppSelector } from '../redux/helper';
import {
  createCustomer,
  getExistingCustomerFromCreateError,
  getSameNameCustomersFromCreateError,
  parseCustomerResponse,
} from '../api/backofficeCustomers';
import { addCustomer } from '../redux/backoffice/customersSlice';
import { upsertCloudCustomerSummary } from '../redux/OrdersSlice';
import { getApiMessage } from '../utils/apiError';
import { rememberCustomerPhone } from './useCustomerPhone';
import type { Customer } from '../types/customers';

export type OrderCustomerCreateInput = {
  name: string;
  phone?: string;
  email?: string;
  /** Save without a phone even though customers with this name exist. */
  allowDuplicateName?: boolean;
};

export type OrderCustomerCreateResult = {
  customer: Customer;
  /** The phone already belonged to this customer; nothing new was saved. */
  existed: boolean;
  /** Past orders typed with this name that the server linked to the customer. */
  ordersLinked: number;
};

/** Orders the server counts for a customer it just saved; 0 when it sent none. */
const linkedOrderCount = (customer: Customer): number =>
  Number((customer as { bp_order_count?: number }).bp_order_count) || 0;

/**
 * Saves a customer from the Save Order sheet with the signed-in employee's
 * own login (every role may create customers) and puts them in the lists the
 * sheet and the back office read. Rejects with a message to show; when the
 * name is already saved and no phone was sent, the error also carries those
 * customers as `sameNameCustomers`.
 */
export const useCreateOrderCustomer = () => {
  const axiosprivate = useAxiosPrivate();
  const { auth } = useAuth();
  const dispatch = useAppDispatch();
  const locationId = useAppSelector(state =>
    String(state.location.currentLocation?._id || ''),
  );

  const createOrderCustomer = useCallback(
    async (
      input: OrderCustomerCreateInput,
    ): Promise<OrderCustomerCreateResult> => {
      if (!locationId) throw new Error('No location selected');
      const name = input.name.trim();
      const phone = String(input.phone || '').trim();
      const email = String(input.email || '').trim();
      try {
        const res = await createCustomer(
          axiosprivate as any,
          locationId,
          {
            bp_customer_name: name,
            ...(phone ? { bp_customer_phone_number: phone } : {}),
            ...(email ? { bp_customer_email_address: email } : {}),
            ...(input.allowDuplicateName ? { allow_duplicate_name: true } : {}),
          },
          { accessToken: auth?.accessToken },
        );
        const ok = res?.data?.success === true || res?.status === 201;
        const created =
          parseCustomerResponse(res)?.customer || res?.data?.customer;
        if (!ok || !created?._id) {
          throw new Error(
            String(res?.data?.message || 'Failed to add customer'),
          );
        }
        const ordersLinked = linkedOrderCount(created);
        dispatch(addCustomer(created));
        dispatch(
          upsertCloudCustomerSummary({
            _id: created._id,
            bp_customer_name: created.bp_customer_name,
            bp_order_count: ordersLinked,
          }),
        );
        rememberCustomerPhone(created._id, created.bp_customer_phone_number);
        return { customer: created, existed: false, ordersLinked };
      } catch (e: any) {
        const existing = getExistingCustomerFromCreateError(e);
        if (existing) {
          rememberCustomerPhone(
            existing._id,
            existing.bp_customer_phone_number,
          );
          dispatch(
            upsertCloudCustomerSummary({
              _id: existing._id,
              bp_customer_name: existing.bp_customer_name,
              bp_order_count: linkedOrderCount(existing),
            }),
          );
          // Nothing was created, so no past order changed hands.
          return { customer: existing, existed: true, ordersLinked: 0 };
        }
        const sameName = getSameNameCustomersFromCreateError(e);
        if (sameName) {
          throw Object.assign(
            new Error(
              getApiMessage(e, 'A customer with this name already exists'),
            ),
            { sameNameCustomers: sameName },
          );
        }
        throw new Error(getApiMessage(e, 'Failed to add customer'));
      }
    },
    [auth?.accessToken, axiosprivate, dispatch, locationId],
  );

  return { createOrderCustomer, locationId } as const;
};

export default useCreateOrderCustomer;
